<template>
  <div class="client-holder">
    <section
      v-if="cms && cms.collection.length > 0"
      :id="cms.scroll_id"
      v-editable="cms"
      :class="[name]"
    >
      <!-- Add ref to the scroll container -->
      <div ref="scrollContainer" class="two-row-scroll">
        <div
          v-for="(item, index) in cms.collection"
          :key="index"
          class="icon-container"
        >
          <a-image-v2
            v-if="show && (item.image || {}).filename"
            :src="item.image.filename"
            :alt="item.image.alt"
            :class="`${name}__image`"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Colors from '~/mixins/colors'
import data from '~/components/organisms/o-clients/o-clients-v2.data'

export default {
  name: 'OClientsV2',
  mixins: [Colors],
  props: {
    cms: {
      type: Object,
      default() {
        const config = {}
        return process.env.NODE_ENV === 'development'
          ? { ...data, ...config }
          : config
      },
    },
  },
  data() {
    return {
      name: 'o-clients-v2',
      show: true,
      active: false,
      scrollInterval: null,
      scrollSpeed: 1, // Adjust speed
      direction: 1, // 1 = move right, -1 = move left
      bounceThreshold: 50, // Distance from edges to "bounce" early
      observer: null, // Will hold our IntersectionObserver instance
    }
  },
  mounted() {
    // Only start the scroll when the container is visible
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          // Check if the scrollContainer is intersecting
          if (entry.isIntersecting) {
            this.startAutoScroll()
            // Unobserve once started to avoid repeated triggers
            this.observer.unobserve(this.$refs.scrollContainer)
          }
        }
      })

      this.observer.observe(this.$refs.scrollContainer)
    } else {
      // Fallback for older browsers without IntersectionObserver
      this.startAutoScroll()
    }
  },
  beforeUnmount() {
    // Clear any interval on destroy
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval)
    }
    // Disconnect the observer if it exists
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    startAutoScroll() {
      const container = this.$refs.scrollContainer
      if (!container) return

      // Set initial scroll position
      container.scrollLeft = 0

      this.scrollInterval = setInterval(() => {
        // Calculate the maximum scroll offset
        const maxScrollLeft = container.scrollWidth - container.clientWidth

        // Move left or right
        container.scrollLeft += this.scrollSpeed * this.direction

        // CLAMP & BOUNCE LOGIC with thresholds
        // Right edge threshold
        if (container.scrollLeft >= maxScrollLeft - this.bounceThreshold) {
          container.scrollLeft = maxScrollLeft - this.bounceThreshold
          this.direction = -1 // Flip direction to the left
        }
        // Left edge threshold
        else if (container.scrollLeft <= this.bounceThreshold) {
          container.scrollLeft = this.bounceThreshold
          this.direction = 1 // Flip direction to the right
        }
      }, 16) // ~60fps
    },
  },
}
</script>

<style lang="scss" scoped>
.client-holder {
  background-color: $white; // or any color you prefer
  width: 100%;
}

.two-row-scroll {
  margin: auto;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 25%;
  grid-template-rows: repeat(2, auto);
  overflow-x: hidden; /* horizontal scroll if more columns than fit */
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* smooth scrolling on iOS */
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
}

.icon-container {
  border: 1px solid rgba(22, 26, 29, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 0;
  width: 100%;
  height: 100%;
}
</style>
